import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // null = not yet checked, true = resolved (either user or no-user)
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip the /me check if we're about to process an OAuth callback
    // (session_id is in the URL fragment). AuthCallback will run first, exchange
    // the session_id, and then call refresh() itself. Hitting /me now would get
    // a 401 and flicker the user back to /login.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      /* ignore network errors on logout */
    }
    setUser(null);
  }, []);

  const value = { user, ready, loading: !ready, refresh, refreshMe: refresh, logout, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
